<template>
  <div v-if="isReportMode">
    <ReportItemContainer>
      <template #question>
        <p>{{ question?.text }}</p>
      </template>
      <template #answer>
        <p>{{ reportViewAnswerText }}</p>
      </template>
    </ReportItemContainer>
  </div>
  <div v-else>
    <p v-if="question?.text" class="mb-4">
      {{ question.text }}
      <InfoPopup
        v-if="shouldDisplayInfoIcon"
        :title="question.helpTitle"
        :content="question.helpText"
      />
    </p>
    <PBooleanInput
      :boolean-options="booleanOptions"
      :disabled="isReadOnlyMode"
      @value-selected="emitNewBoolean"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type Ref } from "vue";
import InfoPopup from "@/components/custom/InfoPopup.vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import PBooleanInput from "@/components/custom/PBooleanInput.vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import type { BooleanOption } from "@/models/questions/booleanOption";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";

const props = withDefaults(
  defineProps<{
    questionModel?: QuestionModelDto;
    answer?: string | object | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newValue: string): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();

const question = computed(() => props.questionModel?.question);
const booleanOptions: Ref<BooleanOption[]> = ref(getBooleanOptions());
const shouldDisplayInfoIcon = computed(
  () =>
    question.value != undefined &&
    question.value.helpText &&
    question.value.helpText?.length > 0
);

const reportViewAnswerText = computed((): string =>
  selectedQuestionnaireStore.getTextForAnswerId(
    props.answer?.toString() ?? "",
    props.questionModel?.question.type!
  )
);

function emitNewBoolean(newBoolean: string): void {
  setBooleanOptions(newBoolean);
  emit("qEvent", newBoolean);
}

function getBooleanOptions(): BooleanOption[] {
  const booleanOptions = selectedQuestionnaireStore.getBooleansOptions(
    props.questionModel?.question.type!
  );

  if (props.answer != null && (props.answer as string) != "") {
    booleanOptions.forEach((option) => {
      if (option.id == props.answer) {
        option.checked = true;
      }
    });
  }
  return booleanOptions;
}

function setBooleanOptions(newTrue: string): void {
  booleanOptions.value.forEach(
    (option: BooleanOption) => (option.checked = option.id == newTrue)
  );
}
</script>

<style lang="scss" scoped>
p {
  white-space: pre-line;
}
</style>
